import type { CosmicProductType } from '@/lib/cms/cosmic/types/CosmicProductType'
import { hydra } from '@/lib/services/Hydra'
import type { HydraGetSkuAvailabilityResponse } from '@/lib/services/Hydra/order/getSkuAvailability'
import useGlobalStore from '@/store/useGlobalStore'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { getLocaleFromUrlQuery } from '@/lib/utils/localization/getLocaleFromUrlQuery'

// Hydra docs: https://github.com/Unicity/Wiki/wiki/Order-Terms#calculate-prices-for-items-and-allowed-quantity-for-the-order

type Props = {
  product: CosmicProductType
}

export type ProductAvailabilityType =
  | 'in_stock'
  | 'low_stock'
  | 'out_of_stock'
  | 'back_order'

export const getSkuAvailability = ({
  sku,
  productAvailability,
}: {
  sku?: string
  productAvailability: HydraGetSkuAvailabilityResponse | null
}): ProductAvailabilityType => {
  if (productAvailability === null) return 'out_of_stock'
  if (!sku) return 'out_of_stock'

  const MAX_STOCK_QUANTITY = 99

  const itemAvailability = productAvailability.items
    .flatMap((item) => [...item.lines.items])
    .find((item) => item.item.id.unicity === sku)

  if (!itemAvailability) return 'out_of_stock'

  const {
    quantityDetails: { quantityAvailable, quantityBackordered },
  } = itemAvailability

  if (quantityAvailable > 0) {
    if (quantityAvailable >= MAX_STOCK_QUANTITY) return 'in_stock'
    if (quantityAvailable < MAX_STOCK_QUANTITY && quantityAvailable > 0)
      return 'low_stock'
  }

  if (quantityBackordered > 0) {
    return 'back_order'
  }

  if (quantityAvailable === 0) return 'out_of_stock'

  return 'out_of_stock' // Fallback case
}

export const useGetSkuAvailability = ({ product }: Props) => {
  const router = useRouter()
  const {
    app: { marketExceptions, alpha2 },
    user: { customerType },
  } = useGlobalStore()

  // Derive market from current URL parameters instead of global store
  const { alpha2: currentMarket } = getLocaleFromUrlQuery({
    query: router.query,
  })

  const [isLoading, setIsLoading] = useState(false)
  const [productAvailability, setProductAvailability] =
    useState<HydraGetSkuAvailabilityResponse | null>(null)

  const getSkuAvailabilityRequest = async () => {
    setIsLoading(true)

    const allVariationSkus = product.variations
      .map((variation) => variation.sku)
      .filter((sku): sku is string => sku !== null && typeof sku === 'string') // Type guard ensures only strings are included

    const response = await hydra.getSkuAvailability({
      customerType,
      alpha2,
      skus: allVariationSkus, // Now allVariationSkus is guaranteed to be string[]
      shippingMethod: marketExceptions?.skuAvailabilityShippingMethod,
    })

    if (response?.error) {
      setIsLoading(false)
      setProductAvailability(null)
      return
    }

    if (response?.items.length === 0) {
      setIsLoading(false)
      setProductAvailability(null)
      return
    }

    setProductAvailability(response)

    setIsLoading(false)
  }

  useEffect(() => {
    // If URL and global store are not the same early return
    // Ensure market exceptions are correct
    if (currentMarket !== alpha2) return
    ;(async () => {
      await getSkuAvailabilityRequest()
    })()
  }, [product, currentMarket, alpha2])

  return { isLoading, productAvailability }
}
